import { useState } from "react";
import { EyeOff, Paperclip, Plus, Receipt } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCatalogs, catalogMap } from "@/lib/lookups";
import { COMMISSION_FIELDS, PAY_METHODS, RESOLVER_FIELDS } from "@/lib/conecta";
import { fmtDate, money } from "@/lib/finance";
import { FieldsForm } from "@/components/finance/FieldsForm";
import { Section, EditAction } from "@/components/common/DetailFields";
import { PaymentForm } from "@/components/conecta/PaymentForm";

const Hidden = () => <span className="inline-flex items-center gap-1 c-muted italic"><EyeOff className="w-3 h-3" /> Oculto</span>;
const Amount = ({ v, cur, hidden, testid }) => <span data-testid={testid} className="font-mono tabular-nums">{hidden ? <Hidden /> : money(v, cur)}</span>;

const openFile = async (id) => {
  try { const { data } = await api.get(`/files/${id}`, { responseType: "blob" }); window.open(URL.createObjectURL(data), "_blank"); } catch (e) { toast.error(errMsg(e)); }
};

const Agreement = ({ ag, isOwner, statuses, modalities, onStatus, onPay, onEdit }) => (
  <div data-testid="commission-item" className="rounded-xl border c-line p-3 space-y-2">
    <div className="flex items-start justify-between gap-2">
      <div>
        <p className="font-semibold text-sm">{modalities[ag.modality]}{!ag.amounts_hidden && ag.percentage != null ? ` · ${ag.percentage}%` : ""}</p>
        <p className="text-xs c-text-2">{ag.payer_name || "Quién paga: sin definir"}{ag.confirmed_in_writing ? " · Confirmada por escrito" : ""}</p>
      </div>
      {isOwner ? (
        <select data-testid="commission-status-select" value={ag.status} onChange={(e) => onStatus(ag, e.target.value)} className="c-input !w-auto !py-1.5 !text-xs">
          {Object.entries(statuses).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      ) : <span data-testid="commission-status" className="text-xs font-semibold c-accent">{statuses[ag.status]}</span>}
    </div>
    <div className="grid grid-cols-3 gap-2 text-xs">
      <div><p className="c-muted">Te toca</p><Amount v={ag.expected_amount} cur={ag.currency} hidden={ag.amounts_hidden} testid="commission-expected" /></div>
      <div><p className="c-muted">Cobrado</p><Amount v={ag.collected_amount} cur={ag.currency} hidden={ag.amounts_hidden} testid="commission-collected" /></div>
      <div><p className="c-muted">Pendiente</p><Amount v={ag.pending_amount} cur={ag.currency} hidden={ag.amounts_hidden} testid="commission-pending" /></div>
    </div>
    {ag.breakdown && <p data-testid="commission-breakdown" className="text-[11px] font-mono c-text-2">{ag.breakdown}</p>}
    {ag.payments.length > 0 && (
      <ul className="divide-y c-line border-t c-line" data-testid="payments-list">
        {ag.payments.map((p) => (
          <li key={p.id} data-testid="payment-item" className="py-2 flex justify-between gap-2 text-xs">
            <div>
              <p className="font-semibold">{fmtDate(p.date) || "Sin fecha"} · {PAY_METHODS[p.method] || p.method || "Forma sin definir"}</p>
              <p className="c-muted">{p.invoice_issued ? `Factura ${p.invoice_folio || "sin folio"}` : "Sin factura"}</p>
              <div className="flex gap-3">
                {p.invoice_file_id && isOwner && <button onClick={() => openFile(p.invoice_file_id)} className="c-accent inline-flex items-center gap-1"><Paperclip className="w-3 h-3" />Factura</button>}
                {p.proof_file_id && isOwner && <button onClick={() => openFile(p.proof_file_id)} className="c-accent inline-flex items-center gap-1"><Paperclip className="w-3 h-3" />Comprobante</button>}
              </div>
            </div>
            <Amount v={p.amount} cur={p.currency} hidden={p.amounts_hidden} />
          </li>
        ))}
      </ul>
    )}
    {isOwner && (
      <div className="flex gap-3 pt-1">
        <button data-testid="commission-edit-btn" onClick={() => onEdit(ag)} className="text-xs font-semibold c-accent">Editar acuerdo</button>
        {ag.status === "por_cobrar" && (
          <button data-testid="add-payment-btn" onClick={() => onPay(ag)} className="text-xs font-semibold c-accent inline-flex items-center gap-1"><Receipt className="w-3.5 h-3.5" /> Registrar pago</button>
        )}
      </div>
    )}
  </div>
);

export const CommissionsSection = ({ opp, setOpp }) => {
  const { isOwner } = useAuth();
  const cats = useCatalogs();
  const [form, setForm] = useState(null);
  const [paying, setPaying] = useState(null);
  const [resolverOpen, setResolverOpen] = useState(false);
  const run = async (fn, msg) => { try { setOpp((await fn()).data); toast.success(msg); return true; } catch (e) { toast.error(errMsg(e)); return false; } };

  if (opp.type === "del_despacho") {
    return <Section title="Comisiones" testid="commissions-section"><p data-testid="sigmaat-note" className="text-sm c-text-2">Negocio del despacho: el dinero se controla en SIGMAAT, no aquí.</p></Section>;
  }
  const saveAgreement = async (payload) => {
    const ok = form.id ? await run(() => api.patch(`/commissions/${form.id}`, payload), "Acuerdo actualizado")
      : await run(() => api.post(`/opportunities/${opp.id}/commissions`, payload), "Acuerdo creado");
    if (ok) setForm(null);
  };
  return (
    <Section title={`Comisiones (${opp.commissions.length})`} testid="commissions-section"
      action={isOwner && <button data-testid="add-commission-btn" onClick={() => setForm({})} className="flex items-center gap-1 text-xs font-semibold c-accent"><Plus className="w-3.5 h-3.5" /> Acuerdo</button>}>
      <div className="space-y-3">
        <div className="flex items-center justify-between text-xs rounded-lg bg-[var(--c-elev)] px-3 py-2">
          <span className="c-text-2">Cobrado por quien resuelve</span>
          <span className="flex items-center gap-2">
            <Amount v={opp.resolver_collected_amount} cur={opp.resolver_collected_currency} hidden={opp.amounts_hidden} testid="resolver-collected-amount" />
            {isOwner && <EditAction onClick={() => setResolverOpen(true)} label="" testid="edit-resolver-collected-btn" />}
          </span>
        </div>
        {opp.commissions.length === 0 && <p className="text-sm c-muted">Sin acuerdos de comisión.</p>}
        {opp.commissions.map((ag) => (
          <Agreement key={ag.id} ag={ag} isOwner={isOwner} statuses={catalogMap(cats, "commission_statuses")} modalities={catalogMap(cats, "commission_modalities")}
            onStatus={(a, status) => run(() => api.patch(`/commissions/${a.id}`, { status }), "Estado actualizado")}
            onPay={setPaying} onEdit={setForm} />
        ))}
      </div>
      {isOwner && (
        <>
          <FieldsForm open={!!form} onOpenChange={(o) => !o && setForm(null)} title={form?.id ? "Editar acuerdo" : "Nuevo acuerdo de comisión"} fields={COMMISSION_FIELDS}
            item={form?.id ? form : null} onSubmit={saveAgreement} testid="commission-form-sheet" description="El monto que te toca se calcula con lo cobrado por quien resuelve." />
          <FieldsForm open={resolverOpen} onOpenChange={setResolverOpen} title="Cobrado por quien resuelve" fields={RESOLVER_FIELDS} item={opp} testid="resolver-form-sheet"
            onSubmit={async (p) => { if (!Object.keys(p).length || await run(() => api.patch(`/opportunities/${opp.id}`, p), "Monto registrado")) setResolverOpen(false); }} />
          <PaymentForm open={!!paying} onOpenChange={(o) => !o && setPaying(null)} currency={paying?.currency}
            onSubmit={async (p) => { if (await run(() => api.post(`/commissions/${paying.id}/payments`, p), "Pago registrado")) setPaying(null); }} />
        </>
      )}
    </Section>
  );
};
